/**
 * @fileoverview Arc Mosaico API Endpoints
 * @description Centralized endpoint configuration for Arc Mosaico features
 *
 * ⚠️ IMPORTANT: All Arc Mosaico features depend on feature flags.
 * Always check `useFeatureToggle('arc-mosaico-project')` before using these endpoints.
 */

const ARC_MOSAICO_BASE = '/api/arc_mosaico'

/**
 * Arc Mosaico API Endpoints
 * Namespace: /api/arc_mosaico
 */
export const ARC_MOSAICO_ENDPOINTS = {
  // Authentication
  auth: {
    /** POST - Unified signup (manual + Google) */
    signup: `${ARC_MOSAICO_BASE}/auth/signup`,
    /** POST - Google OAuth signup */
    googleSignup: `${ARC_MOSAICO_BASE}/auth/google_signup`,
    /** POST - Google OAuth signin (existing users) */
    googleSignin: `${ARC_MOSAICO_BASE}/auth/google_signin`,
    /** POST - Clever Instant Login: exchanges the OAuth `code` received on the
     *  callback page ({ code, redirect_uri }) for a Devise session (PUBLIC).
     *  401 bad/expired code · 404 user not provisioned by the roster sync ·
     *  422 invalid request (AR-925) */
    cleverSignin: `${ARC_MOSAICO_BASE}/auth/clever_signin`,
    /** POST - Student signup via invite token */
    inviteSignup: `${ARC_MOSAICO_BASE}/auth/invite_signup`,
    /** POST - Tutor sets password via invite token (PUBLIC) */
    tutorInviteSignup: `${ARC_MOSAICO_BASE}/auth/tutor_invite_signup`,
  },

  // Group Invites
  groupInvites: {
    /** GET - Validate invite token (PUBLIC) */
    validate: (token: string) => `${ARC_MOSAICO_BASE}/group_invites/validate/${token}`,
    /** GET - Get/generate invite for a group (requires tutor auth) */
    show: (groupId: string | number) => `${ARC_MOSAICO_BASE}/group_invites/${groupId}`,
    /** POST - Regenerate invite for a group (requires tutor auth) */
    regenerate: (groupId: string | number) => `${ARC_MOSAICO_BASE}/group_invites/${groupId}/regenerate`,
  },

  // Setup / Onboarding
  setup: {
    /** POST - Complete onboarding (creates account, school, group) */
    completeOnboarding: `${ARC_MOSAICO_BASE}/setup/complete_onboarding`,
  },

  // External Schools
  externalSchools: {
    /** GET - List external schools */
    list: `${ARC_MOSAICO_BASE}/external_schools`,
    /** GET - Search external schools (query: ?q=name&zipcode=12345) */
    search: `${ARC_MOSAICO_BASE}/external_schools/search`,
    /** POST - Create external school */
    create: `${ARC_MOSAICO_BASE}/external_schools`,
  },

  // Grades
  grades: {
    /** GET - List grades from current user's school courses */
    list: `${ARC_MOSAICO_BASE}/grades`,
  },

  // Group Students
  groupStudents: {
    /** POST - Create students in batch for a group */
    createBatch: `${ARC_MOSAICO_BASE}/group_students/create_batch`,
  },

  // Curriculum
  curriculum: {
    /** GET - Get curriculum items (skills and standards) by grade_id */
    items: (gradeId: string | number) => `${ARC_MOSAICO_BASE}/curriculum_items?grade_id=${gradeId}`,
  },

  // Student Requests (Tutor reviews pending invites)
  studentRequests: {
    /** GET - List pending student requests */
    list: `${ARC_MOSAICO_BASE}/student_requests`,
    /** POST - Accept a student request */
    accept: (id: string) => `${ARC_MOSAICO_BASE}/student_requests/${id}/accept`,
    /** POST - Decline a student request */
    decline: (id: string) => `${ARC_MOSAICO_BASE}/student_requests/${id}/decline`,
  },

  // Students CRUD
  students: {
    /** GET - List students (search, group, status, pagination) */
    list: `${ARC_MOSAICO_BASE}/students`,
    /** GET - Show student details */
    show: (id: string) => `${ARC_MOSAICO_BASE}/students/${id}`,
    /** POST - Create a single student */
    create: `${ARC_MOSAICO_BASE}/students`,
    /** PUT - Update student */
    update: (id: string) => `${ARC_MOSAICO_BASE}/students/${id}`,
    /** DELETE - Remove student */
    destroy: (id: string) => `${ARC_MOSAICO_BASE}/students/${id}`,
    /** POST - Batch create students (returns defaultPassword) */
    batch: `${ARC_MOSAICO_BASE}/students/batch`,
    /** POST - Reset student password */
    resetPassword: (id: string) => `${ARC_MOSAICO_BASE}/students/${id}/reset_password`,
  },

  // Groups CRUD
  groups: {
    /** GET - List tutor's groups */
    list: `${ARC_MOSAICO_BASE}/groups`,
    /** GET - Show group details */
    show: (id: string) => `${ARC_MOSAICO_BASE}/groups/${id}`,
    /** POST - Create a new group */
    create: `${ARC_MOSAICO_BASE}/groups`,
    /** PUT - Update group */
    update: (id: string) => `${ARC_MOSAICO_BASE}/groups/${id}`,
    /** DELETE - Remove group */
    destroy: (id: string) => `${ARC_MOSAICO_BASE}/groups/${id}`,
  },

  // Tutors CRUD
  tutors: {
    /** GET - List tutors (search, pagination) */
    list: `${ARC_MOSAICO_BASE}/tutors`,
    /** GET - Show tutor details */
    show: (id: string) => `${ARC_MOSAICO_BASE}/tutors/${id}`,
    /** POST - Batch create tutors */
    batch: `${ARC_MOSAICO_BASE}/tutors/batch`,
    /** PUT - Update tutor */
    update: (id: string) => `${ARC_MOSAICO_BASE}/tutors/${id}`,
    /** DELETE - Remove tutor from school */
    destroy: (id: string) => `${ARC_MOSAICO_BASE}/tutors/${id}`,
  },

  // Tutor Invites
  tutorInvites: {
    /** GET - Get tutor invite token */
    show: `${ARC_MOSAICO_BASE}/tutor_invites`,
    /** POST - Regenerate tutor invite token */
    regenerate: `${ARC_MOSAICO_BASE}/tutor_invites/regenerate`,
    /** GET - Validate tutor invite token (PUBLIC) */
    validate: (token: string) => `${ARC_MOSAICO_BASE}/tutor_invites/validate/${token}`,
  },

  // Student Invites (student-facing)
  studentInvites: {
    /** GET - Check if dashboard is locked + list invites */
    dashboardStatus: `${ARC_MOSAICO_BASE}/student_invites/dashboard_status`,
    /** POST - Submit a tutor invite code */
    submitCode: `${ARC_MOSAICO_BASE}/student_invites/submit_code`,
  },

  // Schools
  schools: {
    /** GET - Show school details (location, countries, cities) */
    show: (id: string) => `${ARC_MOSAICO_BASE}/schools/${id}`,
    /** PUT - Update school (name, zipcode, city_id) */
    update: (id: string) => `${ARC_MOSAICO_BASE}/schools/${id}`,
  },

  // Categories
  categories: {
    /** GET - List all categories */
    list: `${ARC_MOSAICO_BASE}/categories`,
  },

  // Cities
  cities: {
    /** GET - List cities by country (?country_id=uuid) */
    list: `${ARC_MOSAICO_BASE}/cities`,
  },

  // Profile (My Profile page)
  profile: {
    /** GET - Show current user profile */
    show: `${ARC_MOSAICO_BASE}/profile`,
    /** PATCH - Update personal info */
    update: `${ARC_MOSAICO_BASE}/profile`,
    /** PATCH - Upload avatar (base64) */
    updateAvatar: `${ARC_MOSAICO_BASE}/profile/update_avatar`,
    /** PATCH - Change password */
    updatePassword: `${ARC_MOSAICO_BASE}/profile/update_password`,
    /** POST - Connect Google account (uses axios directly, not api helper) */
    connectGoogle: `${ARC_MOSAICO_BASE}/profile/connect_google`,
    /** DELETE - Disconnect Google account */
    disconnectGoogle: `${ARC_MOSAICO_BASE}/profile/disconnect_google`,
  },

  // Projects
  projects: {
    /** GET - List user projects (My projects) */
    list: `${ARC_MOSAICO_BASE}/projects`,
    /** GET - List community templates (Project library) */
    library: `${ARC_MOSAICO_BASE}/projects/library`,
    /** PATCH - Assign own project to students */
    assign: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/assigned`,
    /** GET - Assign published template (creates copy synchronously) */
    assignPublishedTemplate: (id: string) => `${ARC_MOSAICO_BASE}/projects/${id}/assigned_published_template`,
    /** PUT - Mark project as completed */
    complete: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/complete`,
    /** POST - Share project with co-teacher or as copy */
    share: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/share`,
    /** GET - List project teachers (author + co-teachers only) */
    teachers: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/teachers`,
    /** PATCH - Update assigned students */
    updateStudents: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/update_students`,
    /** GET - List active tutors available for sharing (any tutor can access) */
    shareableTutors: `${ARC_MOSAICO_BASE}/projects/shareable_tutors`,
    /** GET - List project participants (teachers + students with grade) */
    participants: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/participants`,
    /** GET - Development tracker: aggregated framework items (optional ?student_id) */
    developmentTracker: (projectId: string) => `${ARC_MOSAICO_BASE}/projects/${projectId}/development_tracker`,
    /** GET - Development tracker: enrolled active students for the selector */
    developmentTrackerStudents: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/development_tracker/students`,
    /** PATCH - Update one final score (final_assessment_score_id) OR bulk
     *  (lv4_item_id + student_ids[]). Body accepts `master_score`,
     *  `justification`, `do_not_assess`. Mirrors `updateAssessmentScores`
     *  but scoped to the project's final assessment (AR-579). */
    updateFinalAssessmentScores: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/update_final_assessment_scores`,
  },

  // Task Track
  taskTrack: {
    /** GET - List groups for task tracker filter */
    groups: `${ARC_MOSAICO_BASE}/task_track/groups`,
    /** GET - List projects by class/group */
    projectsByClass: (groupId: string) => `${ARC_MOSAICO_BASE}/task_track/project_by_class/${groupId}`,
    /** GET - List students by class (optional project filter via query param) */
    studentsByClass: (groupId: string) => `${ARC_MOSAICO_BASE}/task_track/students_by_class/${groupId}`,
    /** GET - Get task track matrix (requires project_id, optional student_id) */
    matrix: `${ARC_MOSAICO_BASE}/task_track`,
    /** GET - AR-897: the logged-in student's own row of the matrix (requires project_id; student profile only) */
    studentMatrix: `${ARC_MOSAICO_BASE}/task_track/student`,
    /** GET - Get deliverable detail (requires session_meeting_activity_id and student_id).
     *  PATCH - AR-924: set `work_completed_at` of the teacher-uploaded delivery (same ids in the body). */
    deliverable: `${ARC_MOSAICO_BASE}/task_track/deliverable`,
    /** POST (multipart) - AR-924: upload `files[]` on the student's behalf (+ optional `work_completed_at`) */
    deliverableFiles: `${ARC_MOSAICO_BASE}/task_track/deliverable/files`,
    /** POST (multipart) - AR-932: upload the same `files[]` for `student_ids[]` at once (group work) */
    deliverableFilesBulk: `${ARC_MOSAICO_BASE}/task_track/deliverable/files/bulk`,
    /** DELETE - AR-924: remove one uploaded file (ids as query params) */
    deliverableFile: (fileId: string) => `${ARC_MOSAICO_BASE}/task_track/deliverable/files/${fileId}`,
    /** POST - AR-924: add a link `{ url }` on the student's behalf */
    deliverableLinks: `${ARC_MOSAICO_BASE}/task_track/deliverable/links`,
    /** DELETE - AR-924: remove one link (ids as query params) */
    deliverableLink: (linkId: string) => `${ARC_MOSAICO_BASE}/task_track/deliverable/links/${linkId}`,
    /** GET - List students for a session meeting (assessment drawer)
     *  Legacy route — não existe versão arc_mosaico, drawer reusa a UI antiga */
    studentsByMeeting: (sessionMeetingId: string) =>
      `/session_meetings/${sessionMeetingId}/list_student_by_meeting`,
    /** GET - Get student assessment details (assessment drawer)
     *  Legacy route — requer meetingId E assessmentId */
    studentAssessment: (sessionMeetingId: string, assessmentId: string) =>
      `/session_meetings/${sessionMeetingId}/student_assessment/${assessmentId}`,
  },

  // Session Meeting Assessments (AR-538 — Teacher Assessment Drawer)
  sessionMeetingAssessments: {
    /** GET - Lesson-level assessment data (activities × standards/skills × students × scores).
     *  Query: optional `student_ids[]`. */
    listAssessmentScores: (sessionMeetingId: string) =>
      `${ARC_MOSAICO_BASE}/session_meetings/${sessionMeetingId}/list_assessment_scores`,
    /** PATCH - Update one assessment (assessment_score_id) OR bulk (lv4_item_id + student_ids[]).
     *  Body accepts `master_score`, `justification`, `do_not_assess`. */
    updateAssessmentScores: (sessionMeetingId: string) =>
      `${ARC_MOSAICO_BASE}/session_meetings/${sessionMeetingId}/update_assessment_scores`,
  },

  // New Report — class-wide Development Report (AR-545)
  newReport: {
    /** GET - List classes (turmas) for the report's class filter */
    groups: `${ARC_MOSAICO_BASE}/new_report/groups`,
    /** GET - Curriculum trees (content/skills) annotated with assessment data.
     *  Query: optional `group_id` (omitted = all classes). */
    curriculum: (groupId?: string) =>
      `${ARC_MOSAICO_BASE}/new_report/curriculum${groupId ? `?group_id=${groupId}` : ''}`,
  },

  // Google Classroom integration (AR-805)
  classroom: {
    /** GET/POST/DELETE - The LOGGED USER's Google Workspace connection for
     *  the selected school (per-user since feat-classroom-user-connection —
     *  a colleague being connected does not make this one `connected`). */
    connection: `${ARC_MOSAICO_BASE}/google_workspace_connection`,
    /** POST - OAuth authorization URL ({ redirect_uri }) */
    authorizationUrl: `${ARC_MOSAICO_BASE}/google_workspace_connection/authorization_url`,
    /** GET - Local synced course catalog (re-synced on each call while
     *  healthy; cached when needs_reconnection). Payload: {courses
     *  [+linked_project], synced_at, connection_status}. Any tutor may list. */
    courses: `${ARC_MOSAICO_BASE}/google_workspace_connection/courses`,
    /** GET - Existing project↔course links ("Linked courses" table; local
     *  data only). Scoped by profile: managers/managing tutors get the whole
     *  school, a regular tutor gets only links backed by their connection. */
    linkedCourses: `${ARC_MOSAICO_BASE}/google_classroom_courses`,
    /** GET/DELETE - Project ↔ Classroom course link card. POST create was
     *  REMOVED (read-only integration — link existing courses only). */
    projectCourse: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/google_classroom_course`,
    /** POST - Link an existing course ({ course_id }) — runs the roster
     *  matching synchronously and returns {course, roster|null, roster_error?}.
     *  422 course_id when the course is already linked to another project. */
    projectCourseLink: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/google_classroom_course/link`,
    /** POST - Re-run the roster mapping (idempotent) */
    projectCourseSyncRoster: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/google_classroom_course/sync_roster`,
    /** PATCH - Toggle per-project sync ({ enabled }) */
    projectCourseToggleSync: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/google_classroom_course/toggle_sync`,
    /** POST - On-demand submissions import for the whole project (phase 3) */
    projectCourseSyncSubmissions: (projectId: string) =>
      `${ARC_MOSAICO_BASE}/projects/${projectId}/google_classroom_course/sync_submissions`,
    /** GET - Files the student posted on the linked course stream
     *  (announcements). Unavailable states are 200 {available:false, reason}.
     *  Not under the arc_mosaico namespace — it is a plain student_tasks route. */
    studentFiles: (studentTaskId: string) =>
      `/api/student_tasks/${studentTaskId}/google_classroom_files`,
  },

  // Account administration (super admin only)
  accounts: {
    /** GET - List accounts (filters: name, connected, roster_connection_id) */
    list: `${ARC_MOSAICO_BASE}/accounts`,
    /** POST - Create an account ({ name }) */
    create: `${ARC_MOSAICO_BASE}/accounts`,
    /** PATCH - Rename an account ({ name }) */
    update: (accountId: string) => `${ARC_MOSAICO_BASE}/accounts/${accountId}`,
  },

  // Clever roster sync administration (super admin only)
  rosterConnections: {
    /** GET - List every district connection with its synced accounts count */
    list: `${ARC_MOSAICO_BASE}/roster_connections`,
    /** POST - Create a standalone district connection ({ district_external_id, ... }) */
    create: `${ARC_MOSAICO_BASE}/roster_connections`,
    /** GET - Connection detail + recent runs */
    show: (connectionId: string) => `${ARC_MOSAICO_BASE}/roster_connections/${connectionId}`,
    /** PATCH - Update sync settings */
    update: (connectionId: string) => `${ARC_MOSAICO_BASE}/roster_connections/${connectionId}`,
    /** GET - Run history for a connection */
    runs: (connectionId: string) =>
      `${ARC_MOSAICO_BASE}/roster_connections/${connectionId}/roster_sync_runs`,
    /** POST - Trigger a sync ({ dry_run, force_archive }) */
    triggerRun: (connectionId: string) =>
      `${ARC_MOSAICO_BASE}/roster_connections/${connectionId}/roster_sync_runs`,
    /** GET - Run detail + failures */
    runDetail: (runId: string) => `${ARC_MOSAICO_BASE}/roster_sync_runs/${runId}`,
  },
} as const

/**
 * Type for endpoint paths
 */
export type ArcMosaicoEndpoint =
  | (typeof ARC_MOSAICO_ENDPOINTS.auth)[keyof typeof ARC_MOSAICO_ENDPOINTS.auth]
  | (typeof ARC_MOSAICO_ENDPOINTS.setup)[keyof typeof ARC_MOSAICO_ENDPOINTS.setup]
  | (typeof ARC_MOSAICO_ENDPOINTS.externalSchools)[keyof typeof ARC_MOSAICO_ENDPOINTS.externalSchools]
  | (typeof ARC_MOSAICO_ENDPOINTS.grades)[keyof typeof ARC_MOSAICO_ENDPOINTS.grades]
  | ReturnType<typeof ARC_MOSAICO_ENDPOINTS.curriculum.items>
